import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { LayoutDashboard, FileText, LogOut, ChevronDown, MapPin, Forward, Ban, BarChart3, Users, Shield, Boxes, Plane, Package, Sun, Moon, Menu, PanelLeftClose, MessageSquare, ScrollText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { LegalLinks } from "./LegalLinks";
import logo from "../assets/twu-logo.png";

const DIVISIONS = [
  { key: "ramp", label: "Ramp", icon: Plane, agentPortals: [
    { to: "/ramp/agents", label: "Agent Portal", testid: "nav-ramp-agent-portal" },
  ] },
  { key: "ops", label: "Operations/Cargo", icon: Boxes, agentPortals: [
    { to: "/ops/agents", label: "Ops Agent Portal", testid: "nav-ops-agent-portal" },
    { to: "/cargo/agents", label: "Cargo Agent Portal", testid: "nav-cargo-agent-portal" },
  ] },
  { key: "provisioning", label: "Provisioning", icon: Package, agentPortals: [
    { to: "/provisioning/agents", label: "Agent Portal", testid: "nav-provisioning-agent-portal" },
  ] },
];

const grievanceChildren = (div) => [
  { to: `/${div}/grievances`, label: "Local Grievances", icon: MapPin },
  { to: `/${div}/fwd-to-office`, label: "FWD to Office", icon: Forward },
  { to: `/${div}/terminations`, label: "Terminations", icon: Ban },
];

const linkClass = (isActive) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
  }`;

function DivisionSection({ division, onNavigate }) {
  const { can } = useAuth();
  const location = useLocation();
  const children = grievanceChildren(division.key);
  const grievActive = children.some((c) => location.pathname === c.to);
  const [open, setOpen] = useState(location.pathname.startsWith(`/${division.key}/`));
  const DivIcon = division.icon;

  if (!can("view") && !can("statistics") && !can("agents")) return null;

  return (
    <div className="pt-3">
      <div className="flex items-center gap-2 px-3 pb-1">
        <DivIcon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{division.label}</span>
      </div>

      {can("view") && (
        <>
          <button
            data-testid={`nav-${division.key}-grievances`}
            onClick={() => setOpen((o) => !o)}
            className={`w-full ${linkClass(grievActive)} justify-between`}
          >
            <span className="flex items-center gap-3"><FileText className="w-4 h-4" /> Grievances</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="ml-3 pl-3 border-l border-border space-y-1 mt-1">
              {children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  end
                  onClick={onNavigate}
                  data-testid={`nav-${division.key}-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
                      isActive ? "text-foreground font-semibold bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  <c.icon className="w-3.5 h-3.5" /> {c.label}
                </NavLink>
              ))}
            </div>
          )}
        </>
      )}

      {can("statistics") && (
        <NavLink to={`/${division.key}/statistics`} end onClick={onNavigate} data-testid={`nav-${division.key}-statistics`} className={({ isActive }) => linkClass(isActive)}>
          <BarChart3 className="w-4 h-4" /> Statistics
        </NavLink>
      )}
      {can("agents") && division.agentPortals.map((ap) => (
        <NavLink key={ap.to} to={ap.to} end onClick={onNavigate} data-testid={ap.testid} className={({ isActive }) => linkClass(isActive)}>
          <Users className="w-4 h-4" /> {ap.label}
        </NavLink>
      ))}
    </div>
  );
}

export function Layout({ children: content }) {
  const { user, logout, isAdmin, isMaster, can } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches) return false;
    return localStorage.getItem("gt_sidebar") !== "closed";
  });

  // Track viewport so the sidebar behaves as a push-panel on desktop and an overlay drawer on mobile.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = (e) => {
      setIsMobile(e.matches);
      setSidebarOpen(e.matches ? false : localStorage.getItem("gt_sidebar") !== "closed");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Only persist the collapse preference for the desktop push-panel.
  useEffect(() => {
    if (!isMobile) localStorage.setItem("gt_sidebar", sidebarOpen ? "open" : "closed");
  }, [sidebarOpen, isMobile]);

  const closeOnMobile = () => { if (isMobile) setSidebarOpen(false); };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center gap-3 px-4 border-b border-border bg-card">
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
          className="flex items-center justify-center w-9 h-9 -ml-1 rounded-sm text-foreground hover:bg-secondary transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <div className="font-head font-extrabold text-sm leading-none tracking-tight truncate">TWU LOCAL 555 BNA</div>
          <div className="text-[9px] tracking-[0.3em] text-muted-foreground mt-0.5">GRIEVANCE TRACKER</div>
        </div>
        <button
          onClick={toggle}
          data-testid="mobile-theme-toggle"
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="ml-auto flex items-center justify-center w-9 h-9 rounded-sm text-foreground hover:bg-secondary transition-colors"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </header>

      {/* Desktop floating "show menu" button (only when collapsed on desktop) */}
      {!sidebarOpen && !isMobile && (
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="sidebar-open"
          title="Show menu"
          className="hidden lg:flex fixed top-4 left-4 z-50 items-center justify-center w-10 h-10 rounded-sm border border-border bg-card shadow-sm hover:bg-secondary transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px]"
        />
      )}

      <aside className={`app-drawer w-64 lg:w-60 max-w-[85vw] shrink-0 border-r border-border bg-card flex flex-col fixed h-screen z-50 transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="relative flex flex-col items-center gap-2 px-5 py-6 border-b border-border">
          <button
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close"
            title="Hide menu"
            className="absolute top-2 right-2 flex items-center justify-center w-8 h-8 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
          <img src={logo} alt="Transport Workers Union Local 555" className="w-40 h-auto" />
          <div className="text-center">
            <div className="font-head font-extrabold text-sm leading-none tracking-tight">TWU LOCAL 555 BNA</div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground mt-1">GRIEVANCE TRACKER</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {can("view") && (
            <NavLink to="/" end onClick={closeOnMobile} data-testid="nav-overview" className={({ isActive }) => linkClass(isActive)}>
              <LayoutDashboard className="w-4 h-4" /> Overview
            </NavLink>
          )}
          {can("statistics") && (
            <NavLink to="/reports" end onClick={closeOnMobile} data-testid="nav-reports" className={({ isActive }) => linkClass(isActive)}>
              <BarChart3 className="w-4 h-4" /> Reports
            </NavLink>
          )}

          {DIVISIONS.map((d) => <DivisionSection key={d.key} division={d} onNavigate={closeOnMobile} />)}

          {isAdmin && (
            <div className="pt-3">
              <NavLink to="/messaging" end onClick={closeOnMobile} data-testid="nav-messaging" className={({ isActive }) => linkClass(isActive)}>
                <MessageSquare className="w-4 h-4" /> Messaging
              </NavLink>
              <NavLink to="/admin" end onClick={closeOnMobile} data-testid="nav-admin-portal" className={({ isActive }) => linkClass(isActive)}>
                <Shield className="w-4 h-4" /> Admin Portal
              </NavLink>
              {isMaster && (
                <NavLink to="/activity" end onClick={closeOnMobile} data-testid="nav-activity-log" className={({ isActive }) => linkClass(isActive)}>
                  <ScrollText className="w-4 h-4" /> Activity Log
                </NavLink>
              )}
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-border" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center text-xs font-bold uppercase">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || "User"}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-sm transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <ChangePasswordDialog />
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-secondary rounded-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className={`app-main-offset flex-1 min-h-screen flex flex-col transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-60" : "ml-0"}`}>
        <div className="flex-1">{content}</div>
        <footer className="border-t border-border px-4 sm:px-6 lg:px-8 py-6 mt-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} TWU Local 555 BNA · Grievance Tracker</p>
            <LegalLinks />
          </div>
        </footer>
      </main>
    </div>
  );
}
